import { Box, HStack, Span } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import EmailOriginCaption from '@/components/ui/email-origin-caption';
import { isValidEmail } from '@/lib/is-valid-email';
import { useActiveContactDetails } from '../profile/use-active-contact-details';
import type { ContactShell } from '../contact-types';
import EditableContactField from './EditableContactField';
import { formatLongDate } from '@/lib/format-long-date';
import { contactAutomaticMessagesNote } from './contact-automatic-messages';

interface ContactDetailsListProps {
  contact: ContactShell['contact'];
}

const LABEL_WIDTH = '84px';

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Span
    flex="none"
    width={LABEL_WIDTH}
    fontFamily="body"
    fontSize="xs"
    color="primary.400"
    whiteSpace="nowrap"
  >
    {children}
  </Span>
);

/** Read-only metadata row; hidden entirely when there is no value (no data source yet). */
const ReadonlyRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <HStack gap="2.5" minH="38px" px="8px">
      <FieldLabel>{label}</FieldLabel>
      <Span flex="1" minW="0" fontFamily="body" fontSize="13px" color="primary.600">
        {value}
      </Span>
    </HStack>
  );
};

/** Loose, international-friendly check; an empty value is always valid (clears). */
const PHONE_RE = /^\+?[\d\s().-]{6,}$/;

/**
 * Contact fields rendered inside the collapsible CONTACT rail section: editable
 * email + phone (outline inputs, per house rules) sourced from the live,
 * brand-scoped contact override, plus read-only added-on metadata from the
 * shell's contact slice. Editing commits a PATCH via
 * `useActiveContactDetails`.
 */
const ContactDetailsList: React.FC<ContactDetailsListProps> = ({ contact }) => {
  const { t, i18n } = useTranslation();
  const { email, emailOrigin, phone, saveEmail, savePhone, editable } = useActiveContactDetails();

  const validateEmail = (input: string): string | null => {
    const trimmed = input.trim();
    if (!trimmed) return null;
    return isValidEmail(trimmed) ? null : t('contactDrawer.rail.contact.invalidEmail');
  };
  const validatePhone = (input: string): string | null => {
    const trimmed = input.trim();
    if (!trimmed) return null;
    return PHONE_RE.test(trimmed) ? null : t('contactDrawer.rail.contact.invalidPhone');
  };

  return (
    <Box display="flex" flexDirection="column" gap="0.5">
      <EditableContactField
        label={t('contactDrawer.rail.contact.email')}
        labelWidth={LABEL_WIDTH}
        value={email}
        placeholder={t('contactDrawer.rail.contact.emailPlaceholder')}
        validate={validateEmail}
        onCommit={saveEmail}
        readOnly={!editable}
      />
      {/* The label width alone misses the field row's 10px gap: pad by both so
          the caption shares the email value's exact left edge (plus the row's
          8px inner padding). */}
      <Box pl={`calc(${LABEL_WIDTH} + 10px + 8px)`} mt="-1.5" mb="1">
        <EmailOriginCaption origin={emailOrigin} hasEmail={!!email} />
      </Box>
      <EditableContactField
        label={t('contactDrawer.rail.contact.phone')}
        labelWidth={LABEL_WIDTH}
        value={phone}
        placeholder={t('contactDrawer.rail.contact.phonePlaceholder')}
        validate={validatePhone}
        onCommit={savePhone}
        readOnly={!editable}
      />
      <ReadonlyRow
        label={t('contactDrawer.rail.contact.addedOn')}
        value={formatLongDate(contact.addedOn, i18n.language)}
      />
      <ReadonlyRow
        label={t('contactDrawer.rail.contact.automaticMessages')}
        value={contactAutomaticMessagesNote(contact.automaticMessages, t, i18n.language)}
      />
    </Box>
  );
};

export default ContactDetailsList;
